import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Skeleton,
    Pagination,
    Box,
    Button,
    useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTokenTrades } from '../../hooks';
import { formatTokenAmount, transformToBTC, timeAgo } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';

const TokenTrades = ({ tokenId }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const {
        data: trades,
        loading,
        error,
        page,
        setPage,
        hasNextPage
    } = useTokenTrades(tokenId, 10);
    const navigate = useNavigate();
    const { price: btcPrice } = useBitcoinPrice();

    if (loading) {
        return (
            <Skeleton
                variant="rectangular"
                sx={{
                    borderRadius: 2,
                    width: '100%',
                    height: 300,
                }}
            />
        );
    }
    if (error) {
        return (
            <Typography color="error" sx={{ m: 2 }}>
                {t('common.error')}: {error.message}
            </Typography>
        );
    }

    return (
        <Card
            sx={{
                borderRadius: 2,
                boxShadow: 3,
                m: 2,
            }}
        >
            <CardContent>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 'bold', mb: 2 }}
                >
                    {t('token.trades')}
                </Typography>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('table.user')}</TableCell>
                            <TableCell>{t('table.type')}</TableCell>
                            <TableCell>{t('table.tokenAmount')}</TableCell>
                            <TableCell>{t('table.btcAmount')}</TableCell>
                            <TableCell>{t('table.usdValue')}</TableCell>
                            <TableCell>{t('table.price')}</TableCell>
                            <TableCell>{t('table.time')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {trades.map((trade, idx) => (
                            <TableRow
                                key={idx}
                                hover
                                onClick={() => window.open(`/users/${ trade.user }`)}
                                sx={{
                                    cursor: 'pointer',
                                }}
                            >
                                <TableCell>
                                    <Button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(`/users/${ trade.user }`);
                                        }}
                                        sx={{
                                            p: 0,
                                            textTransform: 'none',
                                            fontWeight: 400,
                                            color: 'text.secondary',
                                        }}
                                        size="small"
                                    >
                                        {trade.user.slice(0, 4)}...{trade.user.slice(-4)}
                                    </Button>
                                </TableCell>
                                <TableCell>{trade.type}</TableCell>
                                <TableCell>
                                    {formatTokenAmount(trade.amount_token)}
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2">
                                            {transformToBTC(trade.amount_btc)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ${((trade.amount_btc / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2">
                                            ${((trade.amount_btc / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2">
                                            {(trade.price / 1000).toFixed(2)} sats
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ${(((trade.price / 1000) / 1e8) * btcPrice).toFixed(6)}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {timeAgo(trade.time, t)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={hasNextPage ? page + 1 : page}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        size="small"
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

export default TokenTrades;
